// src/pages/UserBlog.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import blogApi from '../services/blogApi';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardMedia,
    CircularProgress,
    Alert,
    Chip,
    Drawer,
    IconButton,
    Fab,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    FitnessCenter,
    Restaurant,
    EmojiEvents,
    Article,
    Menu as MenuIcon,
    Close,
    CalendarToday,
    Person
} from '@mui/icons-material';
import UserNavbar from '../components/UserNavbar';
import SideMenu from '../components/SideMenu';
import Footer from "../components/Footer";

const UserBlog = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const theme = useTheme();
    const { isDarkMode } = useCustomTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            // Gi cita samo published blogovi, sortirani najnovo-najstaro od backend
            const data = await blogApi.getAllPosts();
            // Extra sigurnost: sortiraj od najnovo kon najstaro
            const sorted = (data || []).sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
            setPosts(sorted);
        } catch (err) {
            console.error('❌ Error loading posts:', err);
            setError('Грешка при вчитување на статиите.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('mk-MK', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Ist sidebar kako UserDashboard
    const menuItems = [
        {
            id: 1,
            title: 'Купи Програми',
            description: 'Прегледај и купи тренинг програми',
            icon: <FitnessCenter sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/buy-programs',
            color: '#ff7eb9',
            available: true,
            badge: 'Популарно'
        },
        {
            id: 2,
            title: 'Купи Диета',
            description: 'Здравствени диететски планови',
            icon: <Restaurant sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-diets',
            color: '#4CAF50',
            available: true,
            badge: 'Препорачано'
        },
        {
            id: 3,
            title: 'Блог',
            description: 'Читај статии, совети и информации за фитнес',
            icon: <Article sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-blog',
            color: '#a5d8ff',
            available: true,
            badge: 'Ново'
        },
        {
            id: 4,
            title: 'Достигнувања',
            description: 'Следете ги вашите достигнувања',
            icon: <EmojiEvents sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/achievements',
            color: '#ffd166',
            available: false,
            badge: 'Наскоро'
        }
    ];

    const handleItemClick = (item) => {
        setSelectedItem(item);
        if (isMobile) setMobileMenuOpen(false);
        if (!item.available) {
            alert('🚧 Оваа функционалност е во развој и наскоро ќе бид�� достапна!');
            return;
        }
        navigate(item.path);
    };

    const handleItemHover = (item) => {
        if (!isMobile) setSelectedItem(item);
    };

    const userName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : 'Корисник';

    if (loading) {
        return (
            <>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                    <UserNavbar />
                </Box>
                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    height: '50vh', backgroundColor: 'background.default'
                }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
            </>
        );
    }

    return (
        <>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                <UserNavbar />
            </Box>

            <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>

                {/* ===== DESKTOP SIDEBAR ===== */}
                {!isMobile && (
                    <Box sx={{
                        width: isTablet ? '280px' : '320px',
                        backgroundColor: 'background.paper',
                        borderRight: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                        position: 'sticky',
                        top: '64px',
                        height: 'calc(100vh - 64px)',
                        overflowY: 'auto',
                        zIndex: 999
                    }}>
                        <SideMenu
                            menuItems={menuItems}
                            selectedItem={selectedItem}
                            onItemClick={handleItemClick}
                            onItemHover={handleItemHover}
                            programsCount={0}
                            userName={userName}
                        />
                    </Box>
                )}

                {/* ===== MOBILE DRAWER ===== */}
                {isMobile && (
                    <>
                        <Drawer
                            anchor="left"
                            open={mobileMenuOpen}
                            onClose={() => setMobileMenuOpen(false)}
                            PaperProps={{
                                sx: {
                                    width: '280px',
                                    backgroundColor: 'background.paper',
                                    borderRight: 1,
                                    borderColor: 'divider'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                                <IconButton onClick={() => setMobileMenuOpen(false)}>
                                    <Close />
                                </IconButton>
                            </Box>
                            <SideMenu
                                menuItems={menuItems}
                                selectedItem={selectedItem}
                                onItemClick={handleItemClick}
                                onItemHover={handleItemHover}
                                programsCount={0}
                                userName={userName}
                            />
                        </Drawer>
                        <Fab
                            color="primary"
                            onClick={() => setMobileMenuOpen(true)}
                            sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1000 }}
                        >
                            <MenuIcon />
                        </Fab>
                    </>
                )}

                {/* ===== MAIN CONTENT ===== */}
                <Box sx={{
                    flex: 1,
                    p: { xs: 2, sm: 3, md: 4 },
                    backgroundColor: 'background.default',
                    overflow: 'auto'
                }}>
                    {/* Header */}
                    <Box sx={{ mb: { xs: 4, md: 5 }, px: { xs: 1, md: 0 } }}>
                        <Typography
                            variant={isMobile ? 'h4' : 'h2'}
                            sx={{
                                fontWeight: 'bold',
                                mb: 1,
                                background: 'linear-gradient(45deg, #a5d8ff, #ff7eb9)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: isMobile ? '1.8rem' : isTablet ? '2.5rem' : '3rem'
                            }}
                        >
                            📰 Блог
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: '500' }}>
                            Совети, статии и информации за фитнес и здравје
                        </Typography>
                    </Box>

                    {/* Error */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
                    )}

                    {/* Posts */}
                    {posts.length === 0 ? (
                        <Box sx={{
                            textAlign: 'center', py: 10,
                            border: '2px dashed',
                            borderColor: 'divider',
                            borderRadius: '16px'
                        }}>
                            <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
                                📭 ��ема објавени статии
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Наскоро ќе бидат достапни нови статии!
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {posts.map((post) => (
                                <Card
                                    key={post.id}
                                    sx={{
                                        backgroundColor: 'background.paper',
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: isDarkMode
                                                ? '0 8px 32px rgba(165,216,255,0.15)'
                                                : '0 8px 32px rgba(165,216,255,0.3)',
                                            transform: 'translateY(-2px)',
                                            borderColor: '#a5d8ff'
                                        }
                                    }}
                                >
                                    {/* Slika ako postoi */}
                                    {post.imageUrl && (
                                        <CardMedia
                                            component="img"
                                            height={isMobile ? 180 : 260}
                                            image={
                                                post.imageUrl.startsWith('http')
                                                    ? post.imageUrl
                                                    : `${process.env.REACT_APP_API_URL}${post.imageUrl}`
                                            }
                                            alt={post.title}
                                            sx={{ objectFit: 'cover' }}
                                        />
                                    )}

                                    <CardContent sx={{ p: isMobile ? 2.5 : 4 }}>
                                        {/* Meta row */}
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center',
                                            gap: 2, mb: 2, flexWrap: 'wrap'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {formatDate(post.createdAt)}
                                                </Typography>
                                            </Box>
                                            {post.author && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {post.author}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Chip
                                                label="Блог"
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(165,216,255,0.15)',
                                                    color: '#a5d8ff',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.7rem'
                                                }}
                                            />
                                        </Box>

                                        {/* Naslov */}
                                        <Typography
                                            variant={isMobile ? 'h5' : 'h4'}
                                            sx={{
                                                fontWeight: 'bold',
                                                color: 'text.primary',
                                                mb: 2,
                                                lineHeight: 1.3,
                                                fontSize: isMobile ? '1.2rem' : '1.6rem'
                                            }}
                                        >
                                            {post.title}
                                        </Typography>

                                        {/* Sodrzhina */}
                                        <Box
                                            sx={{
                                                color: 'text.secondary',
                                                fontSize: isMobile ? '0.9rem' : '1rem',
                                                lineHeight: 1.8,
                                                '& img': { maxWidth: '100%', borderRadius: '8px' },
                                                '& p': { margin: '0 0 12px 0' },
                                                '& h1, & h2, & h3': { color: 'text.primary' },
                                                '& a': { color: '#a5d8ff' }
                                            }}
                                            dangerouslySetInnerHTML={{ __html: post.content }}
                                        />
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}

                    {/* Spacer na kraj */}
                    <Box sx={{ height: { xs: '80px', md: '120px' } }} />
                </Box>
            </Box>
            <Footer />
        </>
    );
};

export default UserBlog;